import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
  FormHelperText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useRegister } from '../../hooks/useRegister';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import RegistrationSuccessDialog from './RegistrationSuccessDialog';

interface RegisterFormProps {
  onLogin?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onLogin }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    authError,
    handleFieldFocus,
    watchedValues,
    showSuccessDialog,
    setShowSuccessDialog,
    registeredEmail,
    navigate,
  } = useRegister();

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {authError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {authError}
        </Alert>
      )}

      {/* ── 基本情報 ── */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 1 }}>
        基本情報
      </Typography>

      <Grid container spacing={2}>
        {/* 名前 */}
        <Grid item xs={12} md={6}>
          <TextField
            {...register('fullName')}
            fullWidth
            label="お名前"
            autoComplete="name"
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
            onFocus={() => handleFieldFocus('fullName')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* ふりがな */}
        <Grid item xs={12} md={6}>
          <TextField
            {...register('furigana')}
            fullWidth
            label="ふりがな"
            autoComplete="off"
            error={!!errors.furigana}
            helperText={errors.furigana?.message}
            onFocus={() => handleFieldFocus('furigana')}
            placeholder="やまだ たろう"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* 電話番号 */}
        <Grid item xs={12}>
          <TextField
            {...register('phone')}
            fullWidth
            label="電話番号"
            type="tel"
            autoComplete="tel"
            error={!!errors.phone}
            helperText={errors.phone?.message ?? '例: 09012345678（ハイフンなし）'}
            onFocus={() => handleFieldFocus('phone')}
            inputProps={{ inputMode: 'tel' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* 生年月日 */}
        <Grid item xs={12}>
          <TextField
            {...register('birthDate')}
            fullWidth
            label="生年月日"
            type="date"
            InputLabelProps={{ shrink: true }}
            error={!!errors.birthDate}
            helperText={errors.birthDate?.message}
            onFocus={() => handleFieldFocus('birthDate')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* 性別 */}
        <Grid item xs={12}>
          <FormControl error={!!errors.gender} required>
            <FormLabel sx={{ fontWeight: 500, mb: 0.5 }}>性別</FormLabel>
            <RadioGroup
              row
              {...register('gender')}
              value={watchedValues.gender ?? ''}
            >
              <FormControlLabel value="male"   control={<Radio />} label="男性" />
              <FormControlLabel value="female" control={<Radio />} label="女性" />
              <FormControlLabel value="other"  control={<Radio />} label="その他" />
            </RadioGroup>
            {errors.gender && (
              <FormHelperText>{errors.gender.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* ── キャリア情報 ── */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        キャリア情報
      </Typography>

      <Grid container spacing={2}>
        {/* 大学 */}
        <Grid item xs={12} md={6}>
          <TextField
            {...register('university')}
            fullWidth
            label="大学"
            autoComplete="organization"
            error={!!errors.university}
            helperText={errors.university?.message}
            onFocus={() => handleFieldFocus('university')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SchoolIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* 学部・学科 */}
        <Grid item xs={12} md={6}>
          <TextField
            {...register('faculty')}
            fullWidth
            label="学部・学科"
            autoComplete="off"
            error={!!errors.faculty}
            helperText={errors.faculty?.message}
            onFocus={() => handleFieldFocus('faculty')}
            placeholder="例: 経済学部 経済学科"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SchoolIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* ── アカウント情報 ── */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        アカウント情報
      </Typography>

      {/* メールアドレス */}
      <TextField
        {...register('email')}
        fullWidth
        label="メールアドレス"
        type="email"
        autoComplete="email"
        margin="normal"
        error={!!errors.email}
        helperText={errors.email?.message ?? 'iCloudメール（@icloud.com等）はご利用いただけません'}
        onFocus={() => handleFieldFocus('email')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* パスワード */}
      <TextField
        {...register('password')}
        fullWidth
        label="パスワード"
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        margin="normal"
        error={!!errors.password}
        helperText={errors.password?.message}
        onFocus={() => handleFieldFocus('password')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* パスワード確認 */}
      <TextField
        {...register('confirmPassword')}
        fullWidth
        label="パスワード確認"
        type={showConfirmPassword ? 'text' : 'password'}
        autoComplete="new-password"
        margin="normal"
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        onFocus={() => handleFieldFocus('confirmPassword')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle confirm password visibility"
                onClick={handleClickShowConfirmPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <PasswordStrengthMeter
        password={watchedValues.password || ''}
        showFeedback={true}
      />

      <Divider sx={{ my: 3 }} />

      {/* 利用規約 */}
      <FormControlLabel
        control={
          <Checkbox
            {...register('agreeToTerms')}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            <Link component={RouterLink} to="/terms/student" target="_blank">
              学生向け利用規約
            </Link>
            と
            <Link component={RouterLink} to="/privacy/student" target="_blank">
              学生向けプライバシーポリシー
            </Link>
            に同意します
          </Typography>
        }
        sx={{ mb: 2, alignItems: 'center' }}
      />

      {errors.agreeToTerms && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
          {errors.agreeToTerms.message}
        </Typography>
      )}

      {/* 送信ボタン */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={!watchedValues.agreeToTerms || isSubmitting}
        sx={{ py: 1.5, position: 'relative', mt: 2 }}
      >
        {isSubmitting ? (
          <>
            <CircularProgress
              size={20}
              sx={{ position: 'absolute', left: '50%', marginLeft: '-10px' }}
            />
            アカウント作成中...
          </>
        ) : (
          'アカウント作成'
        )}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          すでにアカウントをお持ちの方は{' '}
          <Link
            component={RouterLink}
            to="/auth/login"
            onClick={onLogin}
            sx={{ textDecoration: 'none', fontWeight: 'medium' }}
          >
            ログイン
          </Link>
        </Typography>
      </Box>

      <RegistrationSuccessDialog
        open={showSuccessDialog}
        email={registeredEmail}
        onClose={() => setShowSuccessDialog(false)}
        onGoToLogin={() => {
          setShowSuccessDialog(false);
          navigate('/auth/login');
        }}
      />
    </Box>
  );
};

export default RegisterForm;
